import { ExternalLink } from "@/components/ExternalLink";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import i18n from "@/i18n";
import { formatNumber } from "@/features/Table/utils";
import { parseDate } from "@/lib/utils";
import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { createEnum } from "@/lib/utils/createEnum";

export type C5Transaction = {
  id: number;
  date: Date;
  c5_portfolio_client?: string;
  c5_portfolio_id?: number;
  c5_portfolio_name?: string;
  c5_static_datum_id?: number;
  c5_static_datum_name?: string;
  c5_portfolio_reporting_currency?: string;
  quantity?: number;
  market_value_ptf_ccy?: number;
  ptf_currency?: string;
  market_value_local_ccy?: number;
  local_currency?: string;
  category?: string;
  transfer?: string;
  description?: string;
  fees?: number;
  price_local_ccy?: number;
  tax?: number;
  custodian_broker_fee?: number;
  broker_fee?: number;
  other_fee?: number;
  fee_currency?: string;
  fee_type?: string;
};
const c5TransactionFields = createEnum<C5Transaction>();

const t = (key: string, defaultValue: string) =>
  i18n.t(`${Tables.C5_TRANSACTIONS}.columns.${key}`, {
    ns: "tables",
    defaultValue,
  });

export const porfolios_columns: ColumnDef<C5Transaction>[] = [
  {
    accessorKey: c5TransactionFields.id,
    header: t("id", "ID"),
  },
  {
    accessorKey: c5TransactionFields.date,
    header: t("date", "Date"),
    cell: ({ row }) => {
      return (
        <div className="whitespace-nowrap">
          {parseDate(String(row.getValue(c5TransactionFields.date)))}
        </div>
      );
    },
  },
  {
    accessorKey: c5TransactionFields.c5_portfolio_client,
    header: "Client",
  },
  {
    accessorKey: c5TransactionFields.c5_portfolio_name,
    header: t("c5_portfolio_name", "Account"),
    cell: ({ row }) => {
      return (
        <ExternalLink
          href={toWorkspacePath(
            `/admin/${Clients.C5}/${Tables.C5_PORTFOLIOS}/${row.original.c5_portfolio_id}`,
          )}
          className="text-xs"
          underline="hover"
        >
          {row.getValue(c5TransactionFields.c5_portfolio_name)}
        </ExternalLink>
      );
    },
  },
  {
    accessorKey: c5TransactionFields.c5_static_datum_name,
    header: t("c5_static_datum_name", "Asset"),
    cell: ({ row }) => {
      return (
        <ExternalLink
          href={toWorkspacePath(
            `/admin/${Clients.C5}/${Tables.C5_STATIC_DATA}/${row.original.c5_static_datum_id}`,
          )}
          className="text-xs"
          underline="hover"
        >
          {row.getValue(c5TransactionFields.c5_static_datum_name)}
        </ExternalLink>
      );
    },
  },
  {
    accessorKey: c5TransactionFields.quantity,
    header: () => <div className="text-right">{t("quantity", "Quantity")}</div>,
    cell: ({ row }) => {
      return (
        <div className="text-right">
          {formatNumber(row.getValue(c5TransactionFields.quantity))}
        </div>
      );
    },
  },
  {
    accessorKey: c5TransactionFields.market_value_ptf_ccy,
    header: () => (
      <div className="text-right">
        {t("market_value_ptf_ccy", "Market value (reporting ccy)")}
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="text-right">
          {formatNumber(row.getValue(c5TransactionFields.market_value_ptf_ccy))}
        </div>
      );
    },
  },
  {
    header: t(
      "c5_portfolio_reporting_currency",
      "Portfolio reporting currency",
    ),
    accessorKey: c5TransactionFields.c5_portfolio_reporting_currency,
  },
  {
    accessorKey: c5TransactionFields.market_value_local_ccy,
    header: () => (
      <div className="text-right">
        {t("market_value_local_ccy", "Market value local ccy")}
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="text-right">
          {formatNumber(
            row.getValue(c5TransactionFields.market_value_local_ccy),
          )}
        </div>
      );
    },
  },
  {
    accessorKey: c5TransactionFields.local_currency,
    header: t("local_currency", "Local currency"),
  },
  {
    accessorKey: c5TransactionFields.fees,
    header: () => <div className="text-right">{t("fees", "Fees")}</div>,
    cell: ({ row }) => {
      return (
        <div className="text-right">
          {formatNumber(row.getValue(c5TransactionFields.fees))}
        </div>
      );
    },
  },
  {
    accessorKey: c5TransactionFields.fee_currency,
    header: t("fee_currency", "Fee currency"),
  },
  {
    accessorKey: c5TransactionFields.description,
    header: t("description", "Description"),
  },
  {
    accessorKey: c5TransactionFields.category,
    header: t("category", "Movement type"),
  },
  {
    accessorKey: c5TransactionFields.transfer,
    header: t("transfer", "Transfer type"),
    cell: ({ row }) => {
      return (
        <div className="text-left">
          {row.getValue(c5TransactionFields.transfer)}
        </div>
      );
    },
  },
  {
    id: "actions",
    cell: ({ row }) => {
      return (
        <div className="flex justify-end">
          <Link
            href={`${window.location.pathname}/${row.getValue(c5TransactionFields.id)}`}
            className="text-xs"
            underline="hover"
          >
            {i18n.t("actions.edit")}
          </Link>
        </div>
      );
    },
  },
];
